import { useContext, useEffect, useRef, useState } from 'react'

import {
  ChevronLeft,
  ChevronRight,
} from '@awazevr/design-system.components.icon'
import { MediaGallery } from '@awazevr/design-system.components.media-gallery'
import { breakpoints } from '@awazevr/design-system.utils.react'
import { TmsContext } from '@awazevr/mfe-common.contexts.tms'
import type { SearchCriteria } from '@awazevr/mfe-common.types-and-mocks.site-search-service'

import { OptimizelyState } from '@/context/optimizely-decisions-context'
import { usePublicAssetUrl } from '@/utils/helpers'

import { buildTileHref } from './buildTileHref'
import { CORNWALL_PLACES_ID, type ExplorationTile } from './data'
import {
  ExplorationContainer,
  ExplorationHeading,
  ExplorationImage,
  ExplorationLabel,
  ExplorationLink,
  ExplorationNavNext,
  ExplorationNavPrev,
  ExplorationTrack,
} from './ExplorationSuggestions.style'
import { trackExplorationClick, trackExplorationImpression } from './tracking'

export interface ExplorationSuggestionsProps {
  searchedPlace: ExplorationTile
  tiles: ExplorationTile[]
  searchCriteria?: SearchCriteria
  optimizelyState: OptimizelyState
}

const buildHeading = (searchedPlace: ExplorationTile): string =>
  searchedPlace.placesId === CORNWALL_PLACES_ID
    ? 'Destinations in Cornwall'
    : `Destinations near ${searchedPlace.regionName}`

interface ExplorationTileItemProps {
  tile: ExplorationTile
  index: number
  searchCriteria?: SearchCriteria
  onClick: () => void
}

const ExplorationTileItem = ({
  tile,
  index,
  searchCriteria,
  onClick,
}: ExplorationTileItemProps): JSX.Element => {
  const imageUrl = usePublicAssetUrl(tile.imageSrc)

  return (
    <ExplorationLink
      data-index={index}
      href={buildTileHref(tile, searchCriteria)}
      aria-label={tile.regionName}
      data-testid={`exploration-tile-${tile.placesId}`}
      draggable={false}
      onClick={onClick}
    >
      <ExplorationImage
        src={imageUrl}
        alt={tile.regionName}
        loading="lazy"
        draggable={false}
        width={238}
        height={124}
      />
      <ExplorationLabel>{tile.regionName}</ExplorationLabel>
    </ExplorationLink>
  )
}

export const ExplorationSuggestions = ({
  searchedPlace,
  tiles,
  searchCriteria,
  optimizelyState,
}: ExplorationSuggestionsProps): JSX.Element | null => {
  const tms = useContext(TmsContext)

  const lastImpressionKeyRef = useRef<string | null>(null)

  const [ready, setReady] = useState(false)

  useEffect(() => {
    const fallback = globalThis.setTimeout(() => setReady(true), 600)
    return () => globalThis.clearTimeout(fallback)
  }, [])

  useEffect(() => {
    // Rendering the carousel is the impression. Key on searched place + content
    // so a different carousel re-fires once, but re-renders don't double-count.
    if (tiles.length === 0) {
      return
    }

    const impressionKey = `${searchedPlace.placesId}:${tiles.map((tile) => tile.placesId).join(',')}`

    if (lastImpressionKeyRef.current === impressionKey) {
      return
    }

    lastImpressionKeyRef.current = impressionKey
    trackExplorationImpression(searchedPlace, optimizelyState, tms)
  }, [searchedPlace, tiles, optimizelyState, tms])

  if (tiles.length === 0) {
    return null
  }

  const heading = buildHeading(searchedPlace)

  return (
    <ExplorationContainer
      aria-label={heading}
      data-testid="exploration-suggestions"
    >
      <ExplorationHeading>{heading}</ExplorationHeading>
      <ExplorationTrack $ready={ready}>
        <MediaGallery
          onSwiperInit={() => setReady(true)}
          swiperConfig={{
            slidesPerView: 'auto',
            spaceBetween: 12,
            grabCursor: true,
            speed: 250,
            navigation: {
              enabled: true,
              prevEl: '.exploration-nav-prev',
              nextEl: '.exploration-nav-next',
            },
            preventClicks: true,
            preventClicksPropagation: true,
            watchOverflow: true,
            slidesOffsetBefore: 16,
            slidesOffsetAfter: 16,
            breakpoints: {
              [breakpoints[0]]: {
                slidesOffsetBefore: 20,
                slidesOffsetAfter: 20,
              },
            },
          }}
        >
          {tiles.map((tile, index) => (
            <ExplorationTileItem
              key={tile.placesId}
              tile={tile}
              index={index}
              searchCriteria={searchCriteria}
              onClick={() =>
                trackExplorationClick(
                  searchedPlace,
                  tile,
                  index + 1,
                  optimizelyState,
                  tms,
                )
              }
            />
          ))}
        </MediaGallery>
        <ExplorationNavPrev
          type="button"
          className="exploration-nav-prev"
          aria-label="Previous destinations"
        >
          <ChevronLeft iconColour="ColorWhiteOpacity100" />
        </ExplorationNavPrev>
        <ExplorationNavNext
          type="button"
          className="exploration-nav-next"
          aria-label="Next destinations"
        >
          <ChevronRight iconColour="ColorWhiteOpacity100" />
        </ExplorationNavNext>
      </ExplorationTrack>
    </ExplorationContainer>
  )
}
